import React from "react";
import { toJS } from "mobx";
import { CountryWithStateModel, RelatedItemsCategoriesModel, RelatedItemsModel } from "../models";
import SelectItemsPopup from "./SelectItemsPopup";
import { CategoryService, RelatedItemsService } from "../services";
import {
  FlexContainer,
  Input,
  IOption,
  nameof,
  RedButton,
  SapphireBlueButton,
  CreatableSelect as SelectCreateable,
} from "@mindmemobile/mm-shared-global";
import ItemCard from "./ItemCard";

type IProps = {
  item: RelatedItemsModel;
  onUpdate: (data: RelatedItemsModel) => void;
  reltedItemService: RelatedItemsService;
  categoryService?: CategoryService;
  hideIssueCredit?: boolean;
  multiplePriceSelection: boolean;
  sourceId?: string;
  countriesWithState: CountryWithStateModel[];
};
export default class RelatedItemContentRender extends React.Component<IProps> {
  get categoryOptions(): IOption[] {
    const options = (
      this.props.categoryService?.categories?.map((c) => {
        return new RelatedItemsCategoriesModel({ id: c.id, name: c.name });
      }) || []
    ).concat(this.props.reltedItemService?.categories || []);
    return options.map((o) => {
      return { label: o.name, value: o.id };
    });
  }

  onCreditsChange(value: string) {
    this.props.onUpdate(
      new RelatedItemsModel({
        ...this.props.item,
        accountCredits: value,
      })
    );
  }

  onReset() {
    this.props.onUpdate(new RelatedItemsModel());
  }

  selectedCategories(options: RelatedItemsCategoriesModel[]): IOption[] {
    return (
      (options &&
        options.map((o) => {
          return { label: o.name, value: o.id };
        })) ||
      []
    );
  }

  onDeleteItem(item: RelatedItemsCategoriesModel) {
    const clonnedItems = this.props.item.items.map((i) => {
      return new RelatedItemsCategoriesModel({ ...i });
    });
    const index = clonnedItems.findIndex((i) => i.id === item.id);
    clonnedItems.splice(index, 1);
    if (clonnedItems.length > 0) {
      this.props.onUpdate(
        new RelatedItemsModel({ ...this.props.item, items: clonnedItems })
      );
    } else {
      this.props.onUpdate(
        new RelatedItemsModel({
          ...this.props.item,
          items: clonnedItems,
          showSelectItems: false,
        })
      );
    }
  }

  renderInputs(): JSX.Element {
    const option = this.props.item;

    const creditError =
      option.errors?.findIndex(
        (e) => e.field === nameof<RelatedItemsModel>("accountCredits")
      ) > -1;

    const categoryErrors =
      option.errors?.findIndex(
        (e) => e.field === nameof<RelatedItemsModel>("categories")
      ) > -1;
    if (option.showSelectItems) {
      return (
        <FlexContainer
          spacing={20}
          className="label-description-row option-sub-row padding-left-40"
        >
          {option.items.map((item, idx) => {
            const itemData = item.medias.length > 0 ? item.medias[0] : null;
            return (
              <ItemCard
                key={idx}
                url={itemData?.fullURL || ""}
                mediaType={itemData?.type || ""}
                label={item.name}
                price={item.priceDisplay}
                subLabel={
                  item.prices
                    .filter((x) => item.selectedPriceIds.indexOf(x.id) > -1)
                    ?.map((x) => x.name)
                    ?.join() || ""
                }
                showDeleteIcon={true}
                onClick={() => this.onDeleteItem(item)}
              />
            );
          })}
          {option.items.length > 0 && (
            <FlexContainer spacing={10}>
              <SapphireBlueButton
                minWidth="80px"
                height="39px"
                onClick={() => this.openSelectItemPopup()}
                className="media-reset-btn"
              >
                Add Item
              </SapphireBlueButton>
              <RedButton
                minWidth="80px"
                height="39px"
                onClick={() => this.onReset()}
                className="media-reset-btn"
              >
                Reset
              </RedButton>
            </FlexContainer>
          )}
        </FlexContainer>
      );
    }
    if (option.showAccountCredits) {
      return (
        <FlexContainer
          alignItems="center"
          spacing={20}
          className="label-description-row option-sub-row padding-left-40"
        >
          <div>Account Credit</div>
          <Input
            type="number"
            error={creditError}
            value={option.accountCredits}
            onChange={(e) => this.onCreditsChange(e.target.value)}
            className="styled-input option-account-input"
            placeholder="0.00"
          />
          <RedButton
            minWidth="80px"
            height="39px"
            onClick={() => this.onReset()}
          >
            Reset
          </RedButton>
        </FlexContainer>
      );
    }

    if (option.showCategories) {
      return (
        <FlexContainer
          alignItems="center"
          spacing={20}
          className="label-description-row option-sub-row padding-left-40"
        >
          <div>Categories</div>
          <SelectCreateable
            isMulti={true}
            error={categoryErrors}
            options={this.categoryOptions}
            selectedOptions={this.selectedCategories(option.categories)}
            onChange={(newValue) => this.onCreateOrSelectCategory(newValue)}
            className="option-category-select"
            classPrefix="option-select"
          />
          <RedButton
            minWidth="80px"
            height="39px"
            onClick={() => this.onReset()}
          >
            Reset
          </RedButton>
        </FlexContainer>
      );
    }

    return <></>;
  }

  onCreateOrSelectCategory(newValue: IOption[]) {
    if (newValue.length > 0) {
      const lastValue = newValue[newValue.length - 1];
      if (lastValue.__isNew__) {
        this.props.reltedItemService?.addNewCategory(
          new RelatedItemsCategoriesModel({
            id: "",
            name: lastValue.label,
            isNew: lastValue.__isNew__ === true,
          })
        );
      }
    }

    const selectedOptions =
      newValue &&
      newValue.map((v) => {
        return new RelatedItemsCategoriesModel({
          id: v.__isNew__ === true ? "" : v.value,
          name: v.label,
          isNew: v.__isNew__ === true,
        });
      });

    this.props.onUpdate(
      new RelatedItemsModel({
        ...this.props.item,
        categories: selectedOptions,
      })
    );
  }

  onAddItem(newItem: RelatedItemsCategoriesModel) {
    const { item } = this.props;
    const clonnedItems = item.items.map((i) => {
      return new RelatedItemsCategoriesModel({ ...i });
    });
    if (!clonnedItems.find((i) => i.id === newItem.id)) {
      clonnedItems.push(newItem);
    }
    this.props.onUpdate(
      new RelatedItemsModel({
        ...item,
        items: clonnedItems,
        showSelectItemsPopup: false,
      })
    );
  }

  closeSelectItemPopup() {
    const showSelectItemsValue =
      this.props.item.items.length > 0 ? true : false;
    this.props.onUpdate(
      new RelatedItemsModel({
        ...this.props.item,
        showSelectItems: showSelectItemsValue,
        showSelectItemsPopup: false,
      })
    );
  }

  openSelectItemPopup() {
    this.props.onUpdate(
      new RelatedItemsModel({
        ...this.props.item,
        showSelectItems: true,
        showSelectItemsPopup: true,
      })
    );
  }

  get body(): React.ReactNode {
    return this.renderInputs();
  }

  get modalContent(): React.ReactNode {
    return (
      <>
        {this.props.item.showSelectItemsPopup && (
          <SelectItemsPopup
            sourceId={this.props.sourceId}
            exceptItems={this.props.item.items}
            multiplePriceSelection={this.props.multiplePriceSelection}
            onClose={() => this.closeSelectItemPopup()}
            onAddClick={(items: RelatedItemsCategoriesModel) =>
              this.onAddItem(toJS(items))
            }
            countriesWithState={this.props.countriesWithState}
          />
        )}
      </>
    );
  }

  render() {
    return (
      <>
        {this.modalContent}
        {this.body}
      </>
    );
  }
}
